import { Outlet } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import SOSOverlay from './SOSOverlay';
import Drawer from './responsive/Drawer';
import { useIsMobile, MOBILE_QUERY } from './responsive/useMediaQuery';
import { useRealtimeStore } from '../store/realtimeStore';
import clsx from 'clsx';
import { SidebarContext } from '../contexts/SidebarContext';

export default function Layout() {
  const { sosEvents } = useRealtimeStore();
  const isMobile = useIsMobile();
  // Desktop: rail open by default. Mobile: drawer closed by default.
  const [sidebarOpen, setSidebarOpen] = useState(
    () => !(typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches)
  );
  const hasActiveSOS = (sosEvents || []).some((e: any) =>
    e && ['TRIGGERED', 'ACKNOWLEDGED', 'RESPONDING'].includes(e.status)
  );

  // Crossing the breakpoint resets to that shell's default state.
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const toggleSidebar = useCallback(() => setSidebarOpen((o) => !o), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  const sidebarCtx = useMemo(
    () => ({ isOpen: sidebarOpen, toggle: toggleSidebar, close: closeSidebar, isMobile }),
    [sidebarOpen, toggleSidebar, closeSidebar, isMobile]
  );

  return (
    <SidebarContext.Provider value={sidebarCtx}>
      <div className={clsx(
        "min-h-screen bg-command-bg grid-pattern relative",
        hasActiveSOS && "sos-active-blink"
      )}>
        {/* SOS Red Blinking Overlay */}
        {hasActiveSOS && (
          <div className="fixed inset-0 pointer-events-none z-[1] animate-sos-blink">
            <div className="absolute inset-0 bg-red-600/10" />
            <div className="absolute inset-0 border-4 border-red-500/50" />
          </div>
        )}

        {/* Sidebar — desktop rail (pushes content) */}
        <div className={clsx(
          "hidden lg:block fixed left-0 top-0 bottom-0 z-[100] transition-all duration-300 ease-in-out",
          !isMobile && sidebarOpen ? "w-64" : "w-0 overflow-hidden"
        )}>
          {!isMobile && sidebarOpen && <Sidebar />}
        </div>

        {/* Sidebar — mobile off-canvas drawer (overlays content) */}
        {isMobile && (
          <Drawer open={sidebarOpen} onClose={closeSidebar} label="Navigation" widthClass="w-72 max-w-[85vw]">
            <Sidebar onNavigate={closeSidebar} />
          </Drawer>
        )}

        {/* Main Content */}
        <div className={clsx(
          "flex flex-col min-h-screen relative z-10 transition-all duration-300 ease-in-out",
          !isMobile && sidebarOpen ? "lg:ml-64" : "ml-0"
        )}>
          <TopBar />
          <main className="flex-1 flex flex-col p-3 sm:p-4 lg:p-6 overflow-auto">
            <Outlet />
          </main>
        </div>
        <SOSOverlay />

        {/* CSS for SOS blinking animation */}
        <style>{`
          @keyframes sos-blink {
            0%, 100% { opacity: 0; }
            50% { opacity: 1; }
          }
          .animate-sos-blink {
            animation: sos-blink 1s ease-in-out infinite;
          }
          .sos-active-blink {
            box-shadow: inset 0 0 100px rgba(239, 68, 68, 0.1);
          }
        `}</style>
      </div>
    </SidebarContext.Provider>
  );
}
